import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function SatisfactionGuarantee() {
  const { t } = useTranslation();
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const steps = [
    {
      icon: 'ri-file-text-line',
      title: t('guarantee.step1Title'),
      description: t('guarantee.step1Desc'),
      badge: t('guarantee.step1Badge')
    },
    {
      icon: 'ri-luggage-cart-line',
      title: t('guarantee.step2Title'),
      description: t('guarantee.step2Desc'),
      badge: t('guarantee.step2Badge')
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 right-20 w-72 h-72 bg-[#4ECDC4]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#FF6B6B]/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header with Guarantee Badge */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-8 mb-12 sm:mb-16">
          <div className="text-center lg:text-left flex-1">
            <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-[#4ECDC4]/20 to-[#44A08D]/20 rounded-full text-[#44A08D] text-xs sm:text-sm font-semibold mb-4">
              <i className="ri-shield-check-line"></i>
              {t('guarantee.badge')}
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('guarantee.title')}
              <span className="bg-gradient-to-r from-[#4ECDC4] to-[#44A08D] bg-clip-text text-transparent"> {t('guarantee.titleHighlight')}</span>
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto lg:mx-0" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('guarantee.subtitle')} <strong className="text-gray-800">{t('guarantee.subtitleBold')}</strong> :
            </p>
          </div>

          {/* Guarantee Seal */}
          <div className="relative flex-shrink-0">
            <div className="w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 relative">
              {/* Outer Ring */}
              <div className="absolute inset-0 rounded-full border-4 border-dashed border-[#4ECDC4]/40 animate-spin" style={{ animationDuration: '20s' }}></div>
              
              {/* Inner Circle */}
              <div className="absolute inset-2 sm:inset-3 bg-gradient-to-br from-[#4ECDC4] to-[#44A08D] rounded-full shadow-2xl shadow-[#4ECDC4]/30 flex flex-col items-center justify-center text-white p-3 sm:p-4">
                <i className="ri-medal-line text-2xl sm:text-3xl mb-1"></i>
                <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-center">Satisfaction</span>
                <span className="text-base sm:text-lg font-bold">Garantie</span>
                <span className="text-[10px] sm:text-xs opacity-80">100%</span>
              </div>

              {/* Decorative Stars */}
              <div className="absolute -top-1 sm:-top-2 -right-1 sm:-right-2 w-6 sm:w-8 h-6 sm:h-8 bg-[#FF6B6B] rounded-full flex items-center justify-center shadow-lg">
                <i className="ri-star-fill text-white text-xs sm:text-sm"></i>
              </div>
              <div className="absolute -bottom-1 -left-1 w-5 sm:w-6 h-5 sm:h-6 bg-[#FFD93D] rounded-full flex items-center justify-center shadow-lg">
                <i className="ri-star-fill text-white text-[10px] sm:text-xs"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Premium Advertising Visual - Split Design */}
        <div className="mb-12 sm:mb-16 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl relative">
          <div className="w-full h-[300px] sm:h-[400px] lg:h-[500px] relative">
            <img
              src="https://public.readdy.ai/ai/img_res/f43cbf7e471aa50657990505c64e50b7.jpg"
              alt="Garantie 2 ans ABRS - Réparation premium bagages"
              title="Garantie 2 ans réparation bagages ABRS même hors garantie constructeur"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20"></div>
            
            {/* Premium Text Overlay */}
            <div className="absolute inset-0 flex items-center justify-center px-4">
              <div className="text-center">
                <div className="inline-block bg-black/80 backdrop-blur-md rounded-xl sm:rounded-2xl px-4 sm:px-8 py-4 sm:py-6 border border-[#FF6B35]/30 shadow-2xl">
                  <p className="text-[#FF6B35] text-xl sm:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2 tracking-wide" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {t('guarantee.warrantyText1')}
                  </p>
                  <p className="text-white/90 text-sm sm:text-base lg:text-xl font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {t('guarantee.warrantyText2')}
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom Quality Badge */}
            <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-8 right-4 sm:right-8 flex items-center justify-center">
              <div className="flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-[#FF6B35] to-[#FF8E53] backdrop-blur-sm rounded-full px-4 sm:px-6 py-2 sm:py-3 shadow-2xl">
                <i className="ri-tools-fill text-white text-base sm:text-xl w-5 sm:w-6 h-5 sm:h-6 flex items-center justify-center"></i>
                <span className="text-xs sm:text-sm font-bold text-white whitespace-nowrap" style={{ fontFamily: 'Poppins, sans-serif' }}>{t('guarantee.qualityBadge')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Two Steps Cards */}
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {steps.map((step, index) => (
            <div
              key={index}
              className="relative group"
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className={`
                bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg border-2 transition-all duration-500 h-full
                ${hoveredCard === index ? 'border-[#4ECDC4] shadow-xl shadow-[#4ECDC4]/20 -translate-y-2' : 'border-gray-100'}
              `}>
                {/* Step Number */}
                <div className="absolute -top-3 sm:-top-4 -left-3 sm:-left-4 w-10 sm:w-12 h-10 sm:h-12 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] rounded-xl sm:rounded-2xl flex items-center justify-center text-white font-bold text-lg sm:text-xl shadow-lg shadow-[#FF6B6B]/30">
                  {index + 1}
                </div>

                {/* Badge */}
                <div className="flex justify-end mb-4">
                  <span className="px-2 sm:px-3 py-1 bg-[#4ECDC4]/10 text-[#44A08D] text-xs font-semibold rounded-full">
                    {step.badge}
                  </span>
                </div>

                {/* Icon */}
                <div className={`
                  w-16 sm:w-20 h-16 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center mb-5 sm:mb-6 transition-all duration-500
                  ${hoveredCard === index ? 'bg-gradient-to-br from-[#4ECDC4] to-[#44A08D]' : 'bg-gradient-to-br from-gray-100 to-gray-50'}
                `}>
                  <i className={`${step.icon} text-3xl sm:text-4xl ${hoveredCard === index ? 'text-white' : 'text-[#4ECDC4]'}`}></i>
                </div>

                {/* Content */}
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {step.description}
                </p>

                {/* Decorative Arrow */}
                {index === 0 && (
                  <div className="hidden md:flex absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 w-10 sm:w-12 h-10 sm:h-12 bg-white rounded-full shadow-lg items-center justify-center z-10">
                    <i className="ri-arrow-right-line text-[#4ECDC4] text-lg sm:text-xl"></i>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Conclusion - Reputation Argument */}
        <div className="relative">
          <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 text-center relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-full h-full" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #4ECDC4 1px, transparent 1px), radial-gradient(circle at 80% 50%, #FF6B6B 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>
            </div>

            {/* Content */}
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/80 text-xs sm:text-sm font-medium mb-4 sm:mb-6">
                <i className="ri-award-line text-[#FFD93D]"></i>
                {t('guarantee.missionBadge')}
              </div>
              
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white font-medium leading-relaxed max-w-4xl mx-auto" style={{ fontFamily: 'Poppins, sans-serif' }}>
                "{t('guarantee.missionQuote')} <span className="text-[#4ECDC4] font-bold">{t('guarantee.missionHighlight1')}</span> {t('guarantee.missionAnd')} <span className="text-[#4ECDC4] font-bold">{t('guarantee.missionHighlight2')}</span> {t('guarantee.missionEnd')} <span className="text-[#FFD93D] font-bold">{t('guarantee.missionHighlight3')}</span>."
              </p>

              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-6 sm:mt-8">
                <div className="flex items-center gap-2 text-white/70">
                  <i className="ri-shield-check-fill text-[#4ECDC4]"></i>
                  <span className="text-xs sm:text-sm">{t('guarantee.trustIndicator1')}</span>
                </div>
                <div className="flex items-center gap-2 text-white/70">
                  <i className="ri-time-fill text-[#FF6B6B]"></i>
                  <span className="text-xs sm:text-sm">{t('guarantee.trustIndicator2')}</span>
                </div>
                <div className="flex items-center gap-2 text-white/70">
                  <i className="ri-star-fill text-[#FFD93D]"></i>
                  <span className="text-xs sm:text-sm">{t('guarantee.trustIndicator3')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
